/**
 * Servicio del modulo Solicitudes a RRHH.
 * Todos los endpoints pasan por api-central (proxy) bajo /solicitudes-rrhh/...
 *
 * Reemplaza la carga de solicitudes que se hacia en Flokzu. El formulario no
 * esta hardcodeado: se arma con el catalogo que publica EMSYS
 * (Operarios/solicitudes_rrhh_catalogo.py), la unica fuente de verdad de que
 * campos tiene cada motivo, cuando se ven y cuando son obligatorios.
 *
 * Las funciones esVisible/esRequerido de este archivo son el espejo de
 * es_visible()/es_requerido() de solicitudes_rrhh_catalogo.py. EMSYS
 * revalida todo, asi que esto es solo la experiencia de carga.
 */
import api from "./api";

// ------------------------------------------------------------
// Tipos del catalogo
// ------------------------------------------------------------

export type TipoCampo =
  | "TEXTO"
  | "TEXTO_LARGO"
  | "ENTERO"
  | "DECIMAL"
  | "FECHA"
  | "BOOL"
  | "OPCION"
  | "ARCHIVO"
  /** Selector de Punto de Servicio: el valor que viaja es el id del PS. */
  | "PS"
  /**
   * Selector de colaborador ACTIVO: viaja el id del operario y EMSYS congela
   * el nombre al guardar. Lo usa "Superior Directo Nuevo".
   */
  | "OPERARIO"
  /**
   * Selector de cargo: la lista sale del DWH y viaja el IDCargo; EMSYS congela
   * el nombre al guardar. Lo usa "Cargo Nuevo".
   */
  | "CARGO"
  /**
   * Grilla de filas. El valor que viaja es un array de objetos, uno por fila.
   * En la app NO se pinta como tabla: se pinta como lista de tarjetas y cada
   * fila se carga en una hoja modal (ver SolicitudRRHHGrilla).
   */
  | "TABLA"
  /**
   * Evaluacion de desempeño (la planilla que antes se adjuntaba en las
   * desvinculaciones). Los conceptos vienen en `campo.evaluacion` y el valor
   * que viaja es un ValorEvaluacion. Ver SolicitudRRHHEvaluacion.
   */
  | "EVALUACION";

/** Tipo de una celda de un campo TABLA. */
export type TipoColumna =
  | "TEXTO"
  | "ENTERO"
  | "DECIMAL"
  | "FECHA"
  | "BOOL"
  | "OPCION"
  /** Busca un colaborador por legajo, cedula o nombre. Viaja el id. */
  | "OPERARIO"
  /** Elige del catalogo de unidades de negocio. Viaja el id. */
  | "UNIDAD_NEGOCIO";

export interface ColumnaCatalogo {
  codigo: string;
  etiqueta: string;
  tipo: TipoColumna;
  requerido: boolean;
  ayuda: string;
  opciones: OpcionCampo[];
  /**
   * Quien completa la columna: "CARGA" el solicitante, "FINANZAS" y "RRHH"
   * sus bandejas en EMSYS. La app solo pinta las de CARGA.
   */
  etapa?: "CARGA" | "FINANZAS" | "RRHH";
}

/**
 * Las columnas que carga el solicitante. Las otras (el Nº de Factura, que
 * pone Finanzas, y el ID de Descuento, que pone RRHH) viajan en el catalogo
 * igual, pero no se piden en el alta.
 *
 * Sin etapa = catalogo viejo: se asume que la carga el solicitante.
 */
export const columnasDeCarga = (campo: {
  columnas?: ColumnaCatalogo[];
}): ColumnaCatalogo[] =>
  (campo.columnas || []).filter((c) => !c.etapa || c.etapa === "CARGA");

/** Una fila cargada de un campo TABLA: {codigo_columna: valor}. */
export type FilaGrilla = Record<string, string>;

export interface OpcionCampo {
  valor: string;
  texto: string;
}

/** Condicion: {codigo_de_otro_campo: [valores_que_la_activan]}. AND entre claves. */
export type Condicion = Record<string, Array<string | boolean>>;

export interface CampoCatalogo {
  codigo: string;
  etiqueta: string;
  tipo: TipoCampo;
  requerido: boolean;
  opciones: OpcionCampo[];
  visible_si: Condicion;
  requerido_si: Condicion;
  ayuda: string;
  /** El formulario lo precarga con los datos del colaborador. */
  prellenado: boolean;
  /**
   * Lo llena EMSYS a partir de los datos del colaborador (antiguedad,
   * asignaciones vigentes). Se muestra de solo lectura y no se envia: lo que
   * mande el cliente se ignora.
   */
  calculado: boolean;
  /** Solo en los campos TABLA: las columnas de la grilla. */
  columnas?: ColumnaCatalogo[];
  /** Solo en los campos TABLA: tope de filas (10 en Solicitud de Descuento). */
  max_filas?: number | null;
  /** Solo en los campos EVALUACION: la planilla a completar. */
  evaluacion?: PlanillaEvaluacion;
}

// ------------------------------------------------------------
// Evaluacion de desempeño (campo EVALUACION)
// ------------------------------------------------------------

export interface ConceptoEvaluacion {
  codigo: string;
  titulo: string;
  /** Vacia en los conceptos de una sola frase. */
  descripcion: string;
}

export interface SeccionEvaluacion {
  codigo: string;
  titulo: string;
  /** Como se llama en la tabla de puntaje final. */
  resumen: string;
  items: ConceptoEvaluacion[];
}

/** La planilla tal como la declara EMSYS (catalogo.esquema_evaluacion). */
export interface PlanillaEvaluacion {
  plantilla: string;
  titulo: string;
  subtitulo: string;
  /** Nunca = 1 ... Siempre = 4. */
  escala: Array<{ valor: number; texto: string }>;
  secciones: SeccionEvaluacion[];
  textos: Array<{ codigo: string; etiqueta: string; requerido: boolean }>;
  /** `menor_a` es el tope (exclusivo) de la proporcion; null = el resto. */
  clasificaciones: Array<{ codigo: string; texto: string; menor_a: number | null }>;
}

/** Lo que se carga y viaja: una respuesta por concepto y los textos. */
export interface ValorEvaluacion {
  items: Record<string, { puntaje: number | null; comentario: string }>;
  textos: Record<string, string>;
  /**
   * Firma del evaluador: PNG en data URL (canvas.toDataURL). La app la exige;
   * EMSYS la revalida y la pide en todo lo que entra por la app.
   */
  firma?: string;
}

export interface MotivoCatalogo {
  codigo: string;
  /**
   * false en los motivos que llevan los colaboradores en una grilla:
   * la pantalla no muestra la tarjeta de colaborador de la cabecera.
   */
  pide_colaborador?: boolean;
  etiqueta: string;
  campos: CampoCatalogo[];
}

export interface Catalogo {
  motivos: MotivoCatalogo[];
}

// ------------------------------------------------------------
// Tipos de solicitud
// ------------------------------------------------------------

export type EstadoSolicitud =
  | "PENDIENTE_JEFE"
  | "PENDIENTE_FINANZAS"
  | "PENDIENTE_RRHH"
  | "DEVUELTA"
  | "APROBADA"
  | "RECHAZADA";

export interface SolicitudFila {
  id: number;
  fecha_registro: string;
  fecha_alta: string;
  motivo: string;
  motivo_texto: string;
  estado: EstadoSolicitud;
  estado_texto: string;
  /**
   * true mientras la solicitud no se resolvio. Lo dice EMSYS, que es el
   * dueno del flujo: deducirlo de una lista de estados propia es lo que hizo
   * que "En Finanzas" y "Para corregir" aparecieran como resueltas.
   */
  esta_abierta?: boolean;
  origen: "APP" | "EMSYS";
  solicitante: string;
  colaborador: string;
  colaborador_legajo: string;
  punto_servicio: string;
  jefe: string;
  sin_jefe: boolean;
}

export interface CampoMostrado {
  etiqueta: string;
  valor: string;
}

export interface AdjuntoSolicitud {
  id: number;
  codigo_campo: string;
  etiqueta_campo: string;
  nombre_archivo: string;
  mime_type: string;
  tamano_bytes: number;
  /** Ruta de EMSYS. La app no la puede seguir: no tiene sesion de EMSYS. */
  url_descarga: string;
  /**
   * URL de Cloudinary firmada, que EMSYS emite recien despues de validar el
   * permiso. Vence en pocos minutos, asi que se vuelve a pedir el detalle
   * antes de abrirla en vez de guardarla.
   */
  url_firmada?: string;
}

export interface EventoSolicitud {
  tipo: string;
  tipo_texto: string;
  timestamp: string;
  autor: string;
  autor_rol: string;
  detalle: string;
}

export interface SolicitudDetalle extends SolicitudFila {
  colaborador_nombre: string;
  colaborador_cedula: string;
  colaborador_ciudad: string;
  colaborador_direccion: string;
  colaborador_telefono: string;
  colaborador_fecha_ingreso: string;
  ps_codigo: string;
  ps_nombre: string;
  comentario_jefe: string;
  resuelto_por_jefe: string;
  fecha_resolucion_jefe: string;
  /** Verificacion de Finanzas (solo Solicitud de Descuento). */
  comentario_finanzas: string;
  resuelto_por_finanzas: string;
  fecha_resolucion_finanzas: string;
  /** Devolucion al solicitante: quien la devolvio y que hay que corregir. */
  devuelta_por: string;
  comentario_devolucion: string;
  fecha_devolucion: string;
  veces_devuelta: number;
  comentario_rrhh: string;
  resuelto_por_rrhh: string;
  fecha_resolucion_rrhh: string;
  campos: CampoMostrado[];
  /**
   * true si esta DEVUELTA y es de quien consulta: puede corregirla y
   * reenviarla. Lo decide EMSYS, no la app.
   */
  puede_reenviar?: boolean;
  /**
   * Lo cargado, con los IDS de los campos que viajan por id (no la etiqueta),
   * para volver a pintar el formulario de correccion. Solo viene cuando
   * `puede_reenviar`.
   */
  valores?: ValoresFormulario;
  /** Texto congelado de cada campo que viaja por id, para los selectores. */
  etiquetas?: Record<string, string>;
  /** Grilla del motivo Solicitud de Descuento (vacia en los demas). */
  descuentos: DescuentoSolicitud[];
  /** Suma de los importes, ya formateada por EMSYS. */
  descuento_total: string;
  /** Evaluacion de desempeño de la desvinculacion; null si no tiene. */
  evaluacion?: EvaluacionSolicitud | null;
  adjuntos: AdjuntoSolicitud[];
  eventos: EventoSolicitud[];
}

/**
 * La evaluacion de desempeño tal como quedo guardada: con la cabecera y el
 * resultado congelados por EMSYS (el puntaje lo calcula el servidor).
 */
export interface EvaluacionSolicitud {
  titulo: string;
  fecha_evaluacion: string;
  evaluado_nombre: string;
  evaluado_cargo: string;
  evaluado_antiguedad: string;
  evaluador_nombre: string;
  evaluador_cargo: string;
  /** data:image/png;base64,... o "" si se cargo desde EMSYS web. */
  firma_evaluador?: string;
  secciones: Array<{
    codigo: string;
    titulo: string;
    resumen: string;
    puntos_totales: number;
    puntos_alcanzados: number;
    porcentaje: string;
    items: Array<{
      codigo: string;
      titulo: string;
      descripcion: string;
      puntaje: number;
      puntaje_texto: string;
      comentario: string;
    }>;
  }>;
  puntos_totales: number;
  puntos_alcanzados: number;
  /** "75.00": con punto decimal, como lo manda EMSYS. */
  porcentaje: string;
  clasificacion: "BAJO" | "PROMEDIO" | "ALTO" | string;
  clasificacion_texto: string;
  textos: Array<{ codigo: string; etiqueta: string; valor: string }>;
}

/** Una fila de la grilla de descuento, tal como quedo guardada. */
export interface DescuentoSolicitud {
  orden: number;
  legajo: string;
  cedula: string;
  nombre: string;
  empresa: string;
  unidad_negocio: string;
  importe: string;
  cantidad_cuotas: number | null;
  /** Lo completa Finanzas al verificar: vacio hasta entonces. */
  numero_factura: string;
  /** Lo completa RRHH al gestionar: vacio hasta entonces. */
  id_descuento: string;
}

export interface Colaborador {
  operario_id: number;
  legajo: string;
  cedula: string;
  nombre: string;
  texto: string;
}

export interface AsignacionColaborador {
  ps_id: number;
  codigo: string;
  nombre: string;
}

export interface DatosColaborador {
  operario_id: number;
  legajo: string;
  nombre: string;
  cedula: string;
  ciudad: string;
  direccion: string;
  telefono: string;
  fecha_ingreso: string;
  cargo_actual: string;
  departamento: string;
  salario_base: string;
  tipo_funcionario: string;
  antiguedad: string;
  superior_directo: string;
  asignaciones: AsignacionColaborador[];
  asignaciones_texto: string;
}

export interface PuntoServicio {
  ps_id: number;
  codigo: string;
  nombre: string;
  /** Fiscal asignado a ese punto de servicio ("" si no tiene). */
  fiscal?: string;
  /** true si es un PS asignado al fiscal (lo que devuelve la busqueda vacia). */
  propio?: boolean;
}

export interface ApiOk<T> {
  ok: true;
  data: T;
}
export interface ApiErr {
  ok: false;
  error: string;
  errores?: Record<string, string>;
}

// ------------------------------------------------------------
// Helpers
// ------------------------------------------------------------

const unwrap = <T,>(payload: ApiOk<T> | ApiErr): T => {
  if (!payload.ok) {
    throw new Error(payload.error || "Error desconocido");
  }
  return payload.data;
};

/**
 * Error de validacion del catalogo: trae el detalle por campo para poder
 * marcarlos en el formulario.
 */
export class ValidacionSolicitudError extends Error {
  errores: Record<string, string>;

  constructor(mensaje: string, errores: Record<string, string>) {
    super(mensaje);
    this.name = "ValidacionSolicitudError";
    this.errores = errores;
  }
}

// ------------------------------------------------------------
// Permiso
// ------------------------------------------------------------

/** Si el usuario puede pedir a RRHH lo decide EMSYS: fiscales y jefes por
 * cargo, y todos los administrativos por el tipo de funcionario del DWH, que
 * la app no conoce. Se consulta una vez por sesion (el Menu se remonta en cada
 * cambio de ruta). "1" / "0"; sin valor = todavia no se sabe. Se limpia al
 * cerrar sesion. Mismo esquema que el permiso de SST. */
export const RRHH_PERMISO_SESSION_KEY = "rrhh_puede_solicitar_session";

export const permisoRrhhEnSesion = (): boolean | null => {
  const v = sessionStorage.getItem(RRHH_PERMISO_SESSION_KEY);
  return v === null ? null : v === "1";
};

let permisoRrhhEnCurso: Promise<boolean | null> | null = null;

export const cargarPermisoRrhh = (): Promise<boolean | null> => {
  const guardado = permisoRrhhEnSesion();
  if (guardado !== null) return Promise.resolve(guardado);
  if (!permisoRrhhEnCurso) {
    permisoRrhhEnCurso = api
      .get<ApiOk<{ puede_solicitar: boolean }> | ApiErr>("solicitudes-rrhh/permiso")
      .then(({ data }) => {
        const puede = Boolean(unwrap(data).puede_solicitar);
        sessionStorage.setItem(RRHH_PERMISO_SESSION_KEY, puede ? "1" : "0");
        return puede;
      })
      .catch(() => null) // sin marcar: se reintenta en la proxima navegacion
      .finally(() => {
        permisoRrhhEnCurso = null;
      });
  }
  return permisoRrhhEnCurso;
};

// ------------------------------------------------------------
// Catalogo
// ------------------------------------------------------------

export const obtenerCatalogo = async (motivo?: string): Promise<Catalogo> => {
  const { data } = await api.get<ApiOk<Catalogo> | ApiErr>("solicitudes-rrhh/catalogo", {
    params: motivo ? { motivo } : undefined,
  });
  return unwrap(data);
};

export const buscarColaboradores = async (q: string): Promise<Colaborador[]> => {
  const { data } = await api.get<ApiOk<Colaborador[]> | ApiErr>(
    "solicitudes-rrhh/colaboradores",
    { params: { q } },
  );
  return unwrap(data);
};

export const obtenerDatosColaborador = async (
  operario_id: number,
): Promise<DatosColaborador> => {
  const { data } = await api.get<ApiOk<DatosColaborador> | ApiErr>(
    `solicitudes-rrhh/colaboradores/${operario_id}/datos`,
  );
  return unwrap(data);
};

/**
 * Puntos de servicio. Sin termino de busqueda EMSYS devuelve los PS asignados
 * al fiscal, que es lo que necesita por defecto en Ubicacion y Nueva
 * Asignacion.
 */
export interface UnidadNegocio {
  id: number;
  nombre: string;
}

/**
 * Unidades de negocio activas, para las celdas UNIDAD_NEGOCIO.
 *
 * Son media docena y no cambian, asi que la pantalla las pide una vez al
 * abrirse en lugar de por cada fila de la grilla.
 */
/** Un cargo de la lista del DWH. */
export interface Cargo {
  cargo_id: number;
  nombre: string;
}

/**
 * Cargos vigentes del DWH, para el campo "Cargo Nuevo".
 *
 * Son alrededor de cien y no cambian seguido, asi que la pantalla los pide una
 * vez al abrir. Se elige de la lista en vez de tipear para que "Cargo Actual"
 * (que viene del DWH) y "Cargo Nuevo" hablen el mismo idioma.
 */
export const listarCargos = async (termino = ""): Promise<Cargo[]> => {
  const { data } = await api.get<ApiOk<Cargo[]> | ApiErr>(
    "solicitudes-rrhh/cargos",
    { params: termino ? { q: termino } : undefined },
  );
  return unwrap(data);
};

export const listarUnidadesNegocio = async (): Promise<UnidadNegocio[]> => {
  const { data } = await api.get<ApiOk<UnidadNegocio[]> | ApiErr>(
    "solicitudes-rrhh/unidades-negocio",
  );
  return unwrap(data);
};

export const buscarPuntosServicio = async (q = ""): Promise<PuntoServicio[]> => {
  const { data } = await api.get<ApiOk<PuntoServicio[]> | ApiErr>(
    "solicitudes-rrhh/puntos-servicio",
    { params: { q } },
  );
  return unwrap(data);
};

// ------------------------------------------------------------
// Solicitudes
// ------------------------------------------------------------

export interface CrearSolicitudBody {
  motivo: string;
  operario_id?: number;
  legajo?: string;
  cedula?: string;
  punto_servicio_id?: number;
  /**
   * Los campos TABLA viajan como array de filas (ver filaParaEnviar) y la
   * EVALUACION como objeto.
   */
  valores: Record<string, ValorEnviado>;
  /** Codigos de campo ARCHIVO que se van a subir en un segundo paso. */
  adjuntos_a_subir?: string[];
}

export type DecisionJefe = "APROBAR" | "RECHAZAR";

/**
 * Lo que el usuario tiene para aprobar como jefe directo.
 *
 * Devuelve lista vacia cuando no hay nada, que es el caso normal: el aviso del
 * inicio no se muestra si esta vacia.
 *
 * Autoriza la designacion, no un permiso: EMSYS filtra por la cedula del jefe
 * que guardo la solicitud (la que salio de la estructura de operaciones), asi
 * que solo llega lo que le toca. Tampoco hace falta usuario en EMSYS: esta es
 * la unica pantalla donde se aprueba.
 */
export const listarPendientesJefe = async (): Promise<SolicitudFila[]> => {
  const { data } = await api.get<ApiOk<SolicitudFila[]> | ApiErr>(
    "solicitudes-rrhh/solicitudes/pendientes-jefe",
  );
  return unwrap(data);
};

/**
 * Aprueba o rechaza como jefe directo. El comentario es obligatorio al
 * rechazar y EMSYS lo revalida.
 *
 * Aprobar no cierra la solicitud: la pasa a gestion de RRHH.
 */
export const resolverComoJefe = async (
  solicitud_id: number,
  decision: DecisionJefe,
  comentario: string,
): Promise<SolicitudDetalle> => {
  const { data } = await api.post<ApiOk<SolicitudDetalle> | ApiErr>(
    `solicitudes-rrhh/solicitudes/${solicitud_id}/jefe-resolver`,
    { decision, comentario },
  );
  return unwrap(data);
};

export const crearSolicitud = async (
  body: CrearSolicitudBody,
): Promise<SolicitudDetalle> => {
  try {
    const { data } = await api.post<ApiOk<SolicitudDetalle> | ApiErr>(
      "solicitudes-rrhh/solicitudes",
      body,
    );
    return unwrap(data);
  } catch (err: unknown) {
    // 422 = el catalogo rechazo los valores; trae el detalle por campo.
    const e = err as { response?: { status?: number; data?: ApiErr } };
    if (e?.response?.status === 422 && e.response.data) {
      throw new ValidacionSolicitudError(
        e.response.data.error || "Revisa los datos del formulario",
        e.response.data.errores || {},
      );
    }
    throw err;
  }
};

/**
 * Corrige una solicitud devuelta y la reenvia.
 *
 * No lleva motivo ni colaborador: esos no se cambian al corregir. EMSYS
 * revalida todo con el mismo motor que el alta y la manda de vuelta a la
 * bandeja que la devolvio.
 */
export const reenviarSolicitud = async (
  solicitud_id: number,
  body: { valores: ValoresFormulario; adjuntos_a_subir: string[] },
): Promise<SolicitudDetalle> => {
  try {
    const { data } = await api.post<ApiOk<SolicitudDetalle> | ApiErr>(
      `solicitudes-rrhh/solicitudes/${solicitud_id}/reenviar`,
      body,
    );
    return unwrap(data);
  } catch (err: unknown) {
    const e = err as { response?: { status?: number; data?: ApiErr } };
    if (e?.response?.status === 422 && e.response.data) {
      throw new ValidacionSolicitudError(
        e.response.data.error || "Revisa los datos del formulario",
        e.response.data.errores || {},
      );
    }
    throw err;
  }
};

export const listarMisSolicitudes = async (): Promise<SolicitudFila[]> => {
  const { data } = await api.get<ApiOk<SolicitudFila[]> | ApiErr>(
    "solicitudes-rrhh/solicitudes/mias",
  );
  return unwrap(data);
};

export const obtenerSolicitud = async (id: number): Promise<SolicitudDetalle> => {
  const { data } = await api.get<ApiOk<SolicitudDetalle> | ApiErr>(
    `solicitudes-rrhh/solicitudes/${id}`,
  );
  return unwrap(data);
};

export const subirAdjunto = async (
  solicitud_id: number,
  codigo_campo: string,
  file: File,
): Promise<unknown> => {
  const form = new FormData();
  form.append("codigo_campo", codigo_campo);
  form.append("archivo", file);
  const { data } = await api.post<ApiOk<unknown> | ApiErr>(
    `solicitudes-rrhh/solicitudes/${solicitud_id}/adjuntos`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return unwrap(data);
};

// ------------------------------------------------------------
// Motor de condiciones (espejo de catalogo.py)
// ------------------------------------------------------------

const VERDADEROS = ["true", "1", "on", "si", "sí", "yes", "t"];
const FALSOS = ["false", "0", "off", "no", "n", "f", ""];

export type ValorCampo =
  | string
  | number
  | boolean
  | null
  | undefined
  /** Campos TABLA: una entrada por fila cargada. */
  | FilaGrilla[]
  /** Campo EVALUACION. */
  | ValorEvaluacion;
export type ValoresFormulario = Record<string, ValorCampo>;

/** Lo que viaja a EMSYS por campo. */
export type ValorEnviado =
  | string
  | number
  | boolean
  | null
  | FilaGrilla[]
  | ValorEvaluacion;

/** true si el valor es el de un campo EVALUACION. */
export const esValorEvaluacion = (valor: ValorCampo): valor is ValorEvaluacion =>
  !!valor && typeof valor === "object" && !Array.isArray(valor) && "items" in valor;

/** Sin ningun concepto respondido ni texto escrito. */
export const evaluacionVacia = (valor: ValorCampo): boolean => {
  if (!esValorEvaluacion(valor)) return true;
  const respondidos = Object.values(valor.items || {}).some(
    (i) => i.puntaje || (i.comentario || "").trim(),
  );
  const escritos = Object.values(valor.textos || {}).some((t) => (t || "").trim());
  return !respondidos && !escritos;
};

export const conceptosEvaluacion = (
  planilla: PlanillaEvaluacion | undefined,
): ConceptoEvaluacion[] =>
  (planilla?.secciones || []).reduce<ConceptoEvaluacion[]>(
    (todos, s) => todos.concat(s.items),
    [],
  );

export interface ResultadoEvaluacion {
  respondidos: number;
  total: number;
  alcanzados: number;
  totales: number;
  /** "75,00" */
  porcentaje: string;
  clasificacion: string;
  clasificacion_texto: string;
}

/**
 * Espejo de catalogo.resultado_evaluacion(), para mostrar el puntaje mientras
 * se carga. El que queda guardado lo calcula EMSYS.
 */
export const resultadoEvaluacion = (
  planilla: PlanillaEvaluacion | undefined,
  valor: ValorCampo,
): ResultadoEvaluacion => {
  const conceptos = conceptosEvaluacion(planilla);
  const items = esValorEvaluacion(valor) ? valor.items : {};
  const maximo = Math.max(0, ...(planilla?.escala || []).map((o) => o.valor));
  const totales = maximo * conceptos.length;
  let alcanzados = 0;
  let respondidos = 0;
  conceptos.forEach((c) => {
    const puntaje = items[c.codigo]?.puntaje;
    if (puntaje) {
      alcanzados += puntaje;
      respondidos += 1;
    }
  });
  const proporcion = totales ? alcanzados / totales : 0;
  const clasificacion =
    (planilla?.clasificaciones || []).find(
      (c) => c.menor_a === null || proporcion < c.menor_a,
    ) || { codigo: "", texto: "" };
  return {
    respondidos,
    total: conceptos.length,
    alcanzados,
    totales,
    porcentaje: (proporcion * 100).toFixed(2).replace(".", ","),
    clasificacion: clasificacion.codigo,
    clasificacion_texto: clasificacion.texto,
  };
};

/**
 * Lo que le falta a una evaluacion, con los mismos criterios que EMSYS
 * (catalogo._normalizar_evaluacion): empezada tiene que estar completa, aunque
 * el motivo no la exija, y firmada (EMSYS pide la firma en lo que entra por
 * la app). null si esta bien.
 */
export const errorEvaluacion = (
  campo: CampoCatalogo,
  valor: ValorCampo,
  requerido: boolean,
): string | null => {
  if (evaluacionVacia(valor)) {
    return requerido ? `${campo.etiqueta} es obligatoria` : null;
  }
  const v = valor as ValorEvaluacion;
  const { respondidos, total } = resultadoEvaluacion(campo.evaluacion, v);
  if (respondidos < total) {
    return `Faltan responder ${total - respondidos} de ${total} conceptos`;
  }
  const faltante = (campo.evaluacion?.textos || []).find(
    (t) => t.requerido && !(v.textos?.[t.codigo] || "").trim(),
  );
  if (faltante) return `${faltante.etiqueta} es obligatorio`;
  if (!(v.firma || "").trim()) return "Falta tu firma como evaluador";
  return null;
};

export const aBool = (valor: ValorCampo): boolean | null => {
  if (valor === null || valor === undefined) return null;
  if (typeof valor === "boolean") return valor;
  const texto = String(valor).trim().toLowerCase();
  if (VERDADEROS.includes(texto)) return true;
  if (FALSOS.includes(texto)) return false;
  return null;
};

/** Espejo de catalogo._valor_activa(). */
const valorActiva = (valor: ValorCampo, esperados: Array<string | boolean>): boolean =>
  esperados.some((esperado) => {
    if (typeof esperado === "boolean") return aBool(valor) === esperado;
    return valor !== null && valor !== undefined && String(valor) === String(esperado);
  });

/** Espejo de catalogo._condicion_cumple(): AND de todas las claves. */
const condicionCumple = (condicion: Condicion, valores: ValoresFormulario): boolean =>
  Object.keys(condicion || {}).every((clave) =>
    valorActiva(valores[clave], condicion[clave]),
  );

export const esVisible = (campo: CampoCatalogo, valores: ValoresFormulario): boolean => {
  if (!campo.visible_si || Object.keys(campo.visible_si).length === 0) return true;
  return condicionCumple(campo.visible_si, valores);
};

export const esRequerido = (campo: CampoCatalogo, valores: ValoresFormulario): boolean => {
  if (campo.requerido) return true;
  if (!campo.requerido_si || Object.keys(campo.requerido_si).length === 0) return false;
  return condicionCumple(campo.requerido_si, valores);
};

export const camposVisibles = (
  motivo: MotivoCatalogo | undefined,
  valores: ValoresFormulario,
): CampoCatalogo[] => {
  if (!motivo) return [];
  return motivo.campos.filter((c) => esVisible(c, valores));
};

/** Valida del lado del cliente lo mismo que valida EMSYS. */
export const validarFormulario = (
  motivo: MotivoCatalogo | undefined,
  valores: ValoresFormulario,
  archivos: Record<string, File | undefined>,
): Record<string, string> => {
  const errores: Record<string, string> = {};
  if (!motivo) return errores;

  motivo.campos.forEach((campo) => {
    // Los calculados los llena EMSYS: no se piden ni se validan.
    if (campo.calculado) return;
    if (!esVisible(campo, valores)) return;

    // Se revisa aunque sea opcional: empezada, tiene que estar completa.
    if (campo.tipo === "EVALUACION") {
      const error = errorEvaluacion(
        campo,
        valores[campo.codigo],
        esRequerido(campo, valores),
      );
      if (error) errores[campo.codigo] = error;
      return;
    }

    if (!esRequerido(campo, valores)) return;

    if (campo.tipo === "ARCHIVO") {
      if (!archivos[campo.codigo]) {
        errores[campo.codigo] = `${campo.etiqueta} es obligatorio`;
      }
      return;
    }
    const valor = valores[campo.codigo];

    if (campo.tipo === "TABLA") {
      const filas = Array.isArray(valor) ? valor : [];
      if (!filas.length) {
        errores[campo.codigo] = `${campo.etiqueta}: agregá al menos uno`;
        return;
      }
      const tope = campo.max_filas || 0;
      if (tope && filas.length > tope) {
        errores[campo.codigo] = `${campo.etiqueta}: hasta ${tope}`;
        return;
      }
      // Se avisa la primera fila incompleta; EMSYS devuelve el detalle
      // completo si igual se manda.
      const obligatorias = columnasDeCarga(campo).filter((c) => c.requerido);
      for (let i = 0; i < filas.length; i += 1) {
        const faltante = obligatorias.find(
          (c) => !String(filas[i][c.codigo] || "").trim(),
        );
        if (faltante) {
          errores[campo.codigo] =
            `Fila ${i + 1}: ${faltante.etiqueta} es obligatorio`;
          return;
        }
      }
      return;
    }

    if (valor === null || valor === undefined || String(valor).trim() === "") {
      errores[campo.codigo] = `${campo.etiqueta} es obligatorio`;
    }
  });

  return errores;
};

/**
 * Deja solo los valores de campos visibles: un campo oculto no viaja a EMSYS,
 * igual que en Flokzu (y EMSYS lo descartaria de todos modos).
 */
/**
 * Deja de una fila de grilla solo las columnas que declara el catalogo.
 *
 * La app guarda ademas las etiquetas visibles (`__etiqueta_*`) para poder
 * pintar la tarjeta sin volver a consultar. EMSYS las ignoraria igual, pero no
 * tiene sentido mandarlas.
 */
const filaParaEnviar = (
  campo: CampoCatalogo,
  fila: FilaGrilla,
): FilaGrilla => {
  const salida: FilaGrilla = {};
  columnasDeCarga(campo).forEach((columna) => {
    const valor = fila[columna.codigo];
    if (valor !== undefined && valor !== null && String(valor).trim() !== "") {
      salida[columna.codigo] = String(valor).trim();
    }
  });
  return salida;
};

export const valoresParaEnviar = (
  motivo: MotivoCatalogo | undefined,
  valores: ValoresFormulario,
): Record<string, ValorEnviado> => {
  const salida: Record<string, ValorEnviado> = {};
  if (!motivo) return salida;

  motivo.campos.forEach((campo) => {
    if (campo.tipo === "ARCHIVO") return;
    // Los calculados los pone EMSYS; mandarlos seria ruido.
    if (campo.calculado) return;
    if (!esVisible(campo, valores)) return;
    const valor = valores[campo.codigo];

    if (campo.tipo === "TABLA") {
      const filas = Array.isArray(valor) ? valor : [];
      if (filas.length) {
        salida[campo.codigo] = filas.map((fila) => filaParaEnviar(campo, fila));
      }
      return;
    }

    if (campo.tipo === "EVALUACION") {
      if (!evaluacionVacia(valor)) salida[campo.codigo] = valor as ValorEvaluacion;
      return;
    }

    if (valor === null || valor === undefined || String(valor).trim() === "") return;
    salida[campo.codigo] = valor as string | number | boolean;
  });

  return salida;
};

/** Codigos de campo ARCHIVO visibles con archivo elegido, para el alta. */
export const adjuntosADeclarar = (
  motivo: MotivoCatalogo | undefined,
  valores: ValoresFormulario,
  archivos: Record<string, File | undefined>,
): string[] => {
  if (!motivo) return [];
  return motivo.campos
    .filter((c) => c.tipo === "ARCHIVO" && esVisible(c, valores) && archivos[c.codigo])
    .map((c) => c.codigo);
};

/**
 * Precarga los campos prellenado=true (editables) y muestra los calculado=true
 * (de solo lectura, para que el fiscal vea lo que va a quedar guardado; el
 * valor real lo pone EMSYS).
 */
export const prellenarDesdeColaborador = (
  motivo: MotivoCatalogo | undefined,
  datos: DatosColaborador,
  valores: ValoresFormulario,
): ValoresFormulario => {
  if (!motivo) return valores;
  const mapa: Record<string, string> = {
    CARGO_ACTUAL: datos.cargo_actual,
    SUPERIOR_DIRECTO: datos.superior_directo,
    ASIGNACIONES_ACTUALES: datos.asignaciones_texto,
  };
  const siguiente: ValoresFormulario = { ...valores };
  motivo.campos.forEach((campo) => {
    if (!campo.prellenado && !campo.calculado) return;
    const propuesto = mapa[campo.codigo];
    if (!propuesto) return;
    const actual = siguiente[campo.codigo];
    const vacio =
      actual === null || actual === undefined || String(actual).trim() === "";
    if (campo.calculado || vacio) {
      siguiente[campo.codigo] = propuesto;
    }
  });
  return siguiente;
};

// ------------------------------------------------------------
// Etiquetas para UI
// ------------------------------------------------------------

/**
 * Estados en los que la solicitud sigue viva. Es el respaldo de
 * `esta_abierta` para un EMSYS que todavia no lo mande; espeja
 * SolicitudRRHH.ESTADOS_ABIERTOS.
 */
const ESTADOS_ABIERTOS: EstadoSolicitud[] = [
  "PENDIENTE_JEFE",
  "PENDIENTE_FINANZAS",
  "PENDIENTE_RRHH",
  "DEVUELTA",
];

/** true si la solicitud todavia no se resolvio. */
export const estaAbierta = (s: {
  estado: EstadoSolicitud;
  esta_abierta?: boolean;
}): boolean =>
  typeof s.esta_abierta === "boolean"
    ? s.esta_abierta
    : ESTADOS_ABIERTOS.indexOf(s.estado) !== -1;

export const etiquetaEstado = (e: EstadoSolicitud): string => {
  const map: Record<EstadoSolicitud, string> = {
    PENDIENTE_JEFE: "Espera al jefe",
    PENDIENTE_FINANZAS: "En Finanzas",
    DEVUELTA: "Para corregir",
    PENDIENTE_RRHH: "En RRHH",
    APROBADA: "Aprobada",
    RECHAZADA: "Rechazada",
  };
  return map[e] || e;
};

export const colorEstado = (e: EstadoSolicitud): string => {
  const map: Record<EstadoSolicitud, string> = {
    PENDIENTE_JEFE: "warning",
    PENDIENTE_FINANZAS: "tertiary",
    DEVUELTA: "warning",
    PENDIENTE_RRHH: "primary",
    APROBADA: "success",
    RECHAZADA: "danger",
  };
  return map[e] || "medium";
};

/** Los dos motivos de desvinculacion que necesitan el visto del jefe. */
export const MOTIVOS_DESVINCULACION_CON_JEFE = [
  "DESPIDO_SIN_CAUSA",
  "NO_APROBO_PERIODO_PRUEBA",
];

export const requiereAprobacionJefe = (
  motivoCodigo: string,
  valores: ValoresFormulario,
): boolean => {
  if (motivoCodigo !== "DESVINCULACION") return false;
  const motivoDesv = valores.MOTIVO_DESVINCULACION;
  return MOTIVOS_DESVINCULACION_CON_JEFE.includes(String(motivoDesv || ""));
};
